import type { DeliveryAreaFee } from '@/lib/services/locations/locations.types'
import type { Polygon, Position } from 'geojson'
import type { GeoJSONStoreFeatures, TerraDraw, TerraDrawExtend } from 'terra-draw'
import { getZIndexForPolygonType } from './constants'

type FeatureId = TerraDrawExtend.FeatureId

/** The Maps classes these helpers fall back to when `window.google.maps` lacks them. */
export type MapsFallback = Partial<
  Pick<google.maps.CoreLibrary, 'LatLng' | 'LatLngBounds' | 'event'> &
    Pick<google.maps.GeocodingLibrary, 'Geocoder'> &
    Pick<google.maps.MapsLibrary, 'MapTypeId'>
>

type MarkerFallback = Partial<
  Pick<google.maps.MarkerLibrary, 'AdvancedMarkerElement' | 'PinElement'>
>

/** A form polygon drawn on the map, and the Terra Draw feature that draws it. */
export interface MapPolygonEntry {
  terraDrawId: FeatureId
  polygonData?: Pick<DeliveryAreaFee, 'reference_id' | 'polygon_type'> | null
}

/**
 * Validates that a coordinate is a valid number
 */
export const isValidCoordinate = (coord: unknown): coord is number => {
  return (
    coord !== null &&
    coord !== undefined &&
    typeof coord === 'number' &&
    !isNaN(coord) &&
    isFinite(coord)
  )
}

/** A GeoJSON position's latitude and longitude; positions always carry both. */
const toLatLngLiteral = ([lng = NaN, lat = NaN]: Position): google.maps.LatLngLiteral => ({
  lat,
  lng,
})

/**
 * Converts GeoJSON Polygon to Google Maps encoded polyline
 * @param geoJsonPolygon - GeoJSON Polygon object with coordinates
 * @param geometry - Google Maps geometry library
 * @param maps - Google Maps library (for LatLng constructor)
 * @returns Encoded polyline string
 */
export const encodePolygonFromGeoJson = (
  geoJsonPolygon: Pick<Polygon, 'coordinates'> | null | undefined,
  geometry: google.maps.GeometryLibrary | null | undefined,
  maps: MapsFallback | null | undefined
): string | null => {
  if (!geometry?.encoding || !geoJsonPolygon || !maps) return null

  try {
    // GeoJSON Polygon format: { type: "Polygon", coordinates: [[[lng, lat], [lng, lat], ...]] }
    // Note: GeoJSON uses [lng, lat] order, Google Maps uses [lat, lng]
    const coordinates = geoJsonPolygon.coordinates?.[0] // First ring (exterior)
    if (!coordinates || coordinates.length < 3) return null

    const utils = getMapsUtilities(maps)
    const { LatLng } = utils
    if (!LatLng) return null

    // Convert GeoJSON coordinates [lng, lat] to Google Maps LatLng objects
    const pathArray = coordinates.map((position) => {
      const { lat, lng } = toLatLngLiteral(position)
      // Handle both function and property access for lat/lng
      if (typeof LatLng === 'function') {
        return new LatLng(lat, lng)
      }
      return { lat, lng }
    })

    // Encode using Google's encoding
    return geometry.encoding.encodePath(pathArray)
  } catch (err) {
    console.error('Error converting GeoJSON to encoded polyline:', err)
    return null
  }
}

/**
 * Converts an array of {lat, lng} objects to Google Maps encoded polyline
 * @param coordinates - Array of coordinate objects
 * @param geometry - Google Maps geometry library
 * @param maps - Google Maps library (for LatLng constructor)
 * @returns Encoded polyline string
 */
export const encodePolygonFromLatLngArray = (
  coordinates: google.maps.LatLngLiteral[] | null | undefined,
  geometry: google.maps.GeometryLibrary | null | undefined,
  maps: MapsFallback | null | undefined
): string | null => {
  if (
    !geometry?.encoding ||
    !coordinates ||
    !Array.isArray(coordinates) ||
    coordinates.length < 3
  ) {
    return null
  }

  try {
    const utils = getMapsUtilities(maps)
    const { LatLng } = utils
    if (!LatLng) return null

    // Convert coordinate objects to Google Maps LatLng objects
    const pathArray = coordinates.map((coord) => {
      if (typeof LatLng === 'function') {
        return new LatLng(coord.lat, coord.lng)
      }
      return { lat: coord.lat, lng: coord.lng }
    })

    // Encode using Google's encoding
    return geometry.encoding.encodePath(pathArray)
  } catch (err) {
    console.error('Error encoding polygon from coordinates:', err)
    return null
  }
}

/**
 * Converts Google Maps encoded polyline to GeoJSON Polygon
 * @param encodedPolyline - Google Maps encoded polyline string
 * @param geometry - Google Maps geometry library
 * @returns GeoJSON Polygon object
 */
export const encodedPolylineToGeoJson = (
  encodedPolyline: string | null | undefined,
  geometry: google.maps.GeometryLibrary | null | undefined
): Polygon | null => {
  if (!geometry?.encoding || !encodedPolyline) {
    return null
  }

  try {
    // Decode the polyline to LatLng objects
    const pathArray = geometry.encoding.decodePath(encodedPolyline)

    if (!pathArray || pathArray.length < 3) {
      return null
    }

    // Convert LatLng objects to GeoJSON coordinates [lng, lat]
    // Round coordinates to 7 decimal places (~1cm precision) to avoid Terra Draw precision errors
    const roundCoordinate = (coord: number) => Math.round(coord * 10000000) / 10000000
    const coordinates = pathArray.map((latLng): Position => {
      return [roundCoordinate(latLng.lng()), roundCoordinate(latLng.lat())] // GeoJSON uses [lng, lat] order
    })

    // Close the polygon if not already closed
    const firstCoord = coordinates[0]
    const lastCoord = coordinates[coordinates.length - 1]
    if (
      firstCoord &&
      lastCoord &&
      (firstCoord[0] !== lastCoord[0] || firstCoord[1] !== lastCoord[1])
    ) {
      coordinates.push(firstCoord)
    }

    return {
      type: 'Polygon',
      coordinates: [coordinates], // GeoJSON Polygon has array of rings
    }
  } catch (err) {
    console.error('[encodedPolylineToGeoJson] Error converting encoded polyline to GeoJSON:', err)
    return null
  }
}

/**
 * Gets Google Maps constructors/utilities with fallbacks
 */
export const getMapsUtilities = (
  maps: MapsFallback | null | undefined,
  markerLibrary: MarkerFallback | null = null
) => {
  return {
    Geocoder: window.google?.maps?.Geocoder || maps?.Geocoder,
    LatLngBounds: window.google?.maps?.LatLngBounds || maps?.LatLngBounds,
    LatLng: window.google?.maps?.LatLng || maps?.LatLng,
    AdvancedMarkerElement:
      markerLibrary?.AdvancedMarkerElement || window.google?.maps?.marker?.AdvancedMarkerElement,
    PinElement: markerLibrary?.PinElement || window.google?.maps?.marker?.PinElement,
    MapTypeId: window.google?.maps?.MapTypeId || maps?.MapTypeId,
    event: window.google?.maps?.event || maps?.event,
  }
}

const WORLD_TILE_SIZE = 256
const DEFAULT_FOCUS_MIN_ZOOM = 3

interface CoordinateBounds {
  center: google.maps.LatLngLiteral
  east: number
  north: number
  south: number
  west: number
}

const getMapViewportSize = (mapInstance: google.maps.Map, padding: number) => {
  const mapElement = mapInstance?.getDiv?.()
  const width = Number(mapElement?.clientWidth)
  const height = Number(mapElement?.clientHeight)

  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return null
  }

  return {
    width: Math.max(1, width - padding * 2),
    height: Math.max(1, height - padding * 2),
  }
}

const toMercatorLatRadians = (lat: number) => {
  const sin = Math.sin((lat * Math.PI) / 180)
  const radX2 = Math.log((1 + sin) / (1 - sin)) / 2

  return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2
}

const getZoomForFraction = (mapPx: number, worldPx: number, fraction: number) => {
  if (!Number.isFinite(fraction) || fraction <= 0) {
    return Infinity
  }

  return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2)
}

const getCoordinateBounds = (coordinates: Position[]): CoordinateBounds | null => {
  let north = -Infinity
  let south = Infinity
  let east = -Infinity
  let west = Infinity
  let hasValidPoint = false

  coordinates.forEach(([lng, lat]) => {
    if (!isValidCoordinate(lat) || !isValidCoordinate(lng)) {
      return
    }

    north = Math.max(north, lat)
    south = Math.min(south, lat)
    east = Math.max(east, lng)
    west = Math.min(west, lng)
    hasValidPoint = true
  })

  if (!hasValidPoint) {
    return null
  }

  return {
    center: {
      lat: (north + south) / 2,
      lng: (east + west) / 2,
    },
    east,
    north,
    south,
    west,
  }
}

const getFocusCameraForBounds = ({
  bounds,
  mapInstance,
  maxZoom,
  minZoom,
  padding,
}: {
  bounds: CoordinateBounds
  mapInstance: google.maps.Map
  maxZoom: number
  minZoom: number
  padding: number
}) => {
  const viewportSize = getMapViewportSize(mapInstance, padding)

  if (!viewportSize) {
    return null
  }

  const latFraction =
    Math.abs(toMercatorLatRadians(bounds.north) - toMercatorLatRadians(bounds.south)) / Math.PI
  const lngDiff =
    bounds.east - bounds.west < 0 ? bounds.east - bounds.west + 360 : bounds.east - bounds.west
  const lngFraction = lngDiff / 360
  const latZoom = getZoomForFraction(viewportSize.height, WORLD_TILE_SIZE, latFraction)
  const lngZoom = getZoomForFraction(viewportSize.width, WORLD_TILE_SIZE, lngFraction)
  const uncappedZoom = Math.min(latZoom, lngZoom)
  const zoom = Number.isFinite(uncappedZoom)
    ? Math.max(minZoom, Math.min(maxZoom, uncappedZoom))
    : maxZoom

  return {
    center: bounds.center,
    zoom,
  }
}

/** Whether a geometry's first coordinates are a ring of positions, as a polygon's are. */
const isRing = (coordinates: Position | Position[]): coordinates is Position[] =>
  coordinates.every((position) => Array.isArray(position))

export const focusMapOnGeoJsonFeature = ({
  feature,
  mapInstance,
  maps,
  maxZoom = 18,
  minZoom = DEFAULT_FOCUS_MIN_ZOOM,
  padding = 80,
}: {
  feature: GeoJSONStoreFeatures | null | undefined
  mapInstance: google.maps.Map | null | undefined
  maps: MapsFallback | null | undefined
  maxZoom?: number
  minZoom?: number
  padding?: number
}): boolean => {
  const coordinates = feature?.geometry?.coordinates?.[0]

  if (
    !mapInstance ||
    !Array.isArray(coordinates) ||
    coordinates.length === 0 ||
    !isRing(coordinates)
  ) {
    return false
  }

  const bounds = getCoordinateBounds(coordinates)
  if (!bounds) {
    return false
  }

  const camera = getFocusCameraForBounds({ bounds, mapInstance, maxZoom, minZoom, padding })
  if (camera) {
    if (typeof mapInstance.moveCamera === 'function') {
      mapInstance.moveCamera(camera)
    } else {
      mapInstance.setCenter?.(camera.center)
      mapInstance.setZoom?.(camera.zoom)
    }

    return true
  }

  const utils = getMapsUtilities(maps)
  const { LatLngBounds } = utils
  if (!LatLngBounds || typeof mapInstance.fitBounds !== 'function') {
    return false
  }

  const googleBounds = new LatLngBounds()
  coordinates.forEach(([lng, lat]) => {
    if (isValidCoordinate(lat) && isValidCoordinate(lng)) {
      googleBounds.extend({ lat, lng })
    }
  })

  mapInstance.fitBounds(googleBounds, padding)

  if (typeof mapInstance.getZoom === 'function' && typeof mapInstance.setZoom === 'function') {
    const currentZoom = mapInstance.getZoom()
    if (Number.isFinite(currentZoom) && currentZoom !== undefined && currentZoom > maxZoom) {
      mapInstance.setZoom(maxZoom)
    }
  }

  return true
}

export const getTerraDrawFeatureById = (
  terraDraw: TerraDraw | null | undefined,
  featureId: FeatureId | null | undefined
): GeoJSONStoreFeatures | null => {
  if (!terraDraw || !featureId) {
    return null
  }

  if (typeof terraDraw.hasFeature === 'function' && !terraDraw.hasFeature(featureId)) {
    return null
  }

  return terraDraw.getSnapshot?.()?.find((feature) => feature.id === featureId) || null
}

export const selectTerraDrawFeature = (
  terraDraw: TerraDraw | null | undefined,
  featureId: FeatureId | null | undefined
): boolean => {
  const feature = getTerraDrawFeatureById(terraDraw, featureId)

  if (!feature || !featureId || typeof terraDraw?.selectFeature !== 'function') {
    return false
  }

  if (feature.properties?.selected === true) {
    return true
  }

  try {
    terraDraw.selectFeature(featureId)
    return true
  } catch {
    return false
  }
}

const getMapEventFeatureId = (mapEvent: google.maps.Data.MouseEvent | null | undefined) => {
  const featureId = mapEvent?.feature?.getId?.()

  if (featureId === null || featureId === undefined) {
    return null
  }

  return String(featureId)
}

const getMapEventLngLat = (mapEvent: google.maps.MapMouseEvent | null | undefined) => {
  const latLng = mapEvent?.latLng
  const lat = typeof latLng?.lat === 'function' ? latLng.lat() : latLng?.lat
  const lng = typeof latLng?.lng === 'function' ? latLng.lng() : latLng?.lng

  if (!isValidCoordinate(lat) || !isValidCoordinate(lng)) {
    return null
  }

  return { lat, lng }
}

const getPolygonZIndex = (polygon: MapPolygonEntry & { feature?: GeoJSONStoreFeatures }) => {
  const featureZIndex = Number(polygon?.feature?.properties?.zIndex)

  if (Number.isFinite(featureZIndex)) {
    return featureZIndex
  }

  return getZIndexForPolygonType(polygon?.polygonData?.polygon_type)
}

export const getTopSelectableTerraDrawFeatureAtMapClick = ({
  terraDraw,
  mapEvent,
  polygons = [],
}: {
  terraDraw: TerraDraw | null | undefined
  mapEvent: google.maps.Data.MouseEvent | null | undefined
  polygons?: MapPolygonEntry[]
}): GeoJSONStoreFeatures | null => {
  if (!terraDraw || !Array.isArray(polygons) || polygons.length === 0) {
    return null
  }

  const clickedFeatureId = getMapEventFeatureId(mapEvent)
  if (clickedFeatureId) {
    const clickedPolygon = polygons.find(
      (polygon) => String(polygon.terraDrawId) === clickedFeatureId
    )
    if (clickedPolygon) {
      return getTerraDrawFeatureById(terraDraw, clickedPolygon.terraDrawId)
    }
  }

  const lngLat = getMapEventLngLat(mapEvent)
  if (!lngLat || typeof terraDraw.getFeaturesAtLngLat !== 'function') {
    return null
  }

  let clickedFeatures: GeoJSONStoreFeatures[] = []
  try {
    clickedFeatures = terraDraw.getFeaturesAtLngLat(lngLat, {
      addClosestCoordinateInfoToProperties: false,
      ignoreClosingPoints: true,
      ignoreCoordinatePoints: true,
      ignoreCurrentlyDrawing: true,
      ignoreSelectFeatures: true,
      ignoreSnappingPoints: true,
      includePolygonsWithinPointerDistance: true,
      pointerDistance: 12,
    })
  } catch {
    return null
  }

  const clickedFeatureIds = new Set(clickedFeatures.map((feature) => String(feature.id)))
  const matchingPolygons = polygons
    .map((polygon, index) => ({
      ...polygon,
      feature: clickedFeatures.find(
        (feature) => String(feature.id) === String(polygon.terraDrawId)
      ),
      order: index,
    }))
    .filter((polygon) => clickedFeatureIds.has(String(polygon.terraDrawId)))
    .sort((a, b) => getPolygonZIndex(a) - getPolygonZIndex(b) || a.order - b.order)

  const topPolygon = matchingPolygons.at(-1)
  if (!topPolygon) {
    return null
  }

  return getTerraDrawFeatureById(terraDraw, topPolygon.terraDrawId)
}

/**
 * Storefront parity helpers
 * These are used for delivery serviceability validation and polygon fee matching.
 * Keep these in this file so locations tooling and POS modal share one source of truth.
 */

/**
 * Checks if coordinates are valid (numbers and finite)
 */
export function isValidCoordinates(lat: unknown, lng: unknown): boolean {
  return isValidCoordinate(lat) && isValidCoordinate(lng)
}
